import React, {
  useEffect,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import throttle from 'lodash.throttle';
import { AppContext } from '@prisjakt/ui-app-context';
import Spinner from '@prisjakt/ui-spinner';

import { ListSection } from '../../../../../../types';
import InfiniteScroll from '../../../../../../components/infinite-scroll';
import RouteContext from '../../../../../../routes/context';
import { ListFilterItem } from '../../types';
import Checkboxes from '../components/checkboxes';
import RadioButtons from '../components/radio-buttons';
import { trackProductCollectionFilterUpdate } from '../../../../tracking';
import { useAggregationSettings } from '../../../../hooks';
import * as S from './styles';
import * as ComponentStyles from '../components/styles';
import * as SectionStyles from '../styles';

type Props = ListSection & {
  useInfiniteScroll?: boolean;
  selectedItems?: ListFilterItem[];
  showTitle?: boolean;
};

const SEARCH_THROTTLE = 750;
const throttledSearch = throttle(
  (id: string, query: string, dispatch: (action: any) => void) =>
    dispatch({
      type: 'AGGREGATION_SEARCH',
      id,
      query: query.length > 1 ? query : '', // one letter searches return empty
    }),
  SEARCH_THROTTLE
);

const List = ({
  id,
  title,
  multiple,
  hasMore,
  items,
  property,
  search,
  useInfiniteScroll,
  selectedItems,
  showTitle,
}: Props) => {
  const { t } = useContext(AppContext);
  const { dispatch, getState } = useContext(RouteContext);
  const aggregationSettings = useAggregationSettings(id);

  const [searchQuery, setSearchQuery] = useState(
    // Set to undefined to not perform a search on mount
    aggregationSettings?.query || undefined
  );

  const onClickShowMore = useCallback(
    () =>
      dispatch({
        type: 'AGGREGATION_MORE_ITEMS',
        id,
        size: items.length * 2,
      }),
    [dispatch, id, items.length]
  );

  const onLoadMore = useCallback(() => hasMore && onClickShowMore(), [
    hasMore,
    onClickShowMore,
  ]);

  const onAggregationSearch = useCallback(
    e => {
      setSearchQuery(e.currentTarget.value);
    },
    [setSearchQuery]
  );

  const onClearAggregationSearch = useCallback(() => setSearchQuery(''), []);

  const getSelectedState = useCallback(
    itemId => {
      const { filters } = getState();

      const existingFilter = filters.find((x: any) => x.property === property);

      if (!existingFilter) {
        return false;
      }

      if (existingFilter.type === 'term') {
        return itemId === existingFilter.id;
      }

      if (existingFilter.type === 'terms') {
        return existingFilter.ids.includes(itemId);
      }

      return false;
    },
    [getState, property]
  );

  const onClickItem = useCallback(
    itemId => {
      trackProductCollectionFilterUpdate(getState().filters, property, itemId);
      dispatch({
        type: 'FILTER_LIST_UPDATE',
        property,
        multiple,
        id: itemId,
      });
    },
    [dispatch, getState, multiple, property]
  );

  const loader = useMemo(
    () =>
      hasMore ? (
        <S.LoaderWrapper>
          <Spinner position="none" size={24} isActive />
        </S.LoaderWrapper>
      ) : null,
    [hasMore]
  );

  useEffect(() => {
    if (searchQuery !== undefined) {
      throttledSearch(id, searchQuery, dispatch);
    }
  }, [searchQuery, id, dispatch]);

  const renderSelectedFilters = () => {
    return (
      // If we had a totalCount from the API, we could just check totalCount > 20 here.
      // This tries to emulate that condition, but it's not bulletproof.
      (hasMore || items.length > 20 || searchQuery !== undefined) &&
      selectedItems && (
        <S.SelectedFiltersWrapper>
          <SectionStyles.SectionTitle slim>
            {t('filter', 'applied-filters.title')}
          </SectionStyles.SectionTitle>
          <S.OptionList>
            <Checkboxes
              items={selectedItems}
              getSelectedState={getSelectedState}
              onClickItem={onClickItem}
            />
          </S.OptionList>
        </S.SelectedFiltersWrapper>
      )
    );
  };

  const renderSearchInput = () => (
    <S.SearchInputWrapper>
      <S.SearchInput
        value={searchQuery}
        type="search"
        onChange={onAggregationSearch}
        placeholder={t('filter', 'filter.search-aggregation', {
          filter: title.toLowerCase(),
        })}
      />

      {searchQuery && searchQuery.length > 0 && (
        <S.ClearButton
          onClick={onClearAggregationSearch}
          aria-label={t('common', 'button.clear')}
        >
          <S.ClearIcon size={16} />
        </S.ClearButton>
      )}
    </S.SearchInputWrapper>
  );

  const renderOptionList = () => (
    <S.OptionList data-test="OptionList">
      {multiple ? (
        <Checkboxes
          items={items}
          getSelectedState={getSelectedState}
          onClickItem={onClickItem}
        />
      ) : (
        <RadioButtons
          items={items}
          getSelectedState={getSelectedState}
          onClickItem={onClickItem}
        />
      )}
      {hasMore && !useInfiniteScroll && (
        <ComponentStyles.OptionListItem key="show-more">
          <ComponentStyles.Option onClick={onClickShowMore}>
            {t('filter', 'properties.quantity-show-more')}
          </ComponentStyles.Option>
        </ComponentStyles.OptionListItem>
      )}
    </S.OptionList>
  );

  const component = (
    <SectionStyles.Section>
      {showTitle && (
        <SectionStyles.SectionTitle>{title}</SectionStyles.SectionTitle>
      )}
      {renderSelectedFilters()}
      {search && renderSearchInput()}
      {renderOptionList()}
    </SectionStyles.Section>
  );

  return useInfiniteScroll ? (
    <InfiniteScroll onLoad={onLoadMore} placeholder={loader}>
      {component}
    </InfiniteScroll>
  ) : (
    component
  );
};

export default List;
